import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getHomePageMeta } from '@/seo/meta';
import { homeData } from '@/data/homeContent';
import { siteManifest } from '@/config/site';
import { getPortfolios } from '@/api/portfolio';
import { localizedName, localizedTitle } from '@/i18n/localize';
import { FadeInUp } from '@/components/ui/FadeInUp';
import AccreditationBar from '@/components/sections/AccreditationBar';
import AccreditationStats from '@/components/sections/AccreditationStats';
import AccreditationShowcase from '@/components/sections/AccreditationShowcase';
import ContactCta from '@/components/sections/ContactCta';
import type { PortfolioItem } from '@/types';

type ServiceItem = { title: string; description: string };
type ProcessStep = { no: string; title: string; description: string };

const PROJECT_FALLBACK = 'https://placehold.co/800x600';

export default function Home() {
  const { t, i18n } = useTranslation();
  const [portfolios, setPortfolios] = useState<PortfolioItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  usePageMeta(getHomePageMeta());

  useEffect(() => {
    window.scrollTo(0, 0);
    getPortfolios()
      .then(setPortfolios)
      .catch(err => setError(err.message || 'Failed to load projects'));
  }, []);

  const services = t('services.items', { returnObjects: true }) as ServiceItem[];
  const pillars = t('whyMgc.items', { returnObjects: true }) as ServiceItem[];
  const steps = t('process.steps', { returnObjects: true }) as ProcessStep[];
  const aboutParagraphs = t('aboutIntro.paragraphs', { returnObjects: true }) as string[];
  const accreditation = siteManifest.accreditation;

  return (
    <>
      {/* Hero + accreditation bar share one full-bleed background image */}
      <div className="hero-stack" style={{ backgroundImage: `url(${homeData.hero.image})` }}>
      <section
        className="hero parallaxie"
        style={{
          background: 'linear-gradient(rgba(25,35,36,0.5), rgba(25,35,36,0.5))',
          minHeight: '58vh',
          padding: '120px 0 50px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-10">
              <div className="hero-content">
                <div className="section-title">
                  <h3 style={{ color: '#fff' }}>{t('home.hero.eyebrow')}</h3>
                  <h1 style={{ whiteSpace: 'pre-line' }}>{t('home.hero.title')}</h1>
                  <p>{t('home.hero.description')}</p>
                </div>
                <div className="hero-btn">
                  <Link to="/portfolio" className="btn-default btn-highlighted">{t('home.hero.cta')}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
        <AccreditationBar glass />
      </div>

      {/* About preview */}
      <section className="mgc-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="about-us-images">
                <div className="about-img-1">
                  <img src={homeData.about.image1} alt={t('aboutIntro.title')} />
                </div>
                <div className="about-img-2">
                  <img src={homeData.about.image2} alt={t('aboutIntro.title')} />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <span className="mgc-eyebrow">{t('aboutIntro.eyebrow')}</span>
              <h2 className="mgc-heading" style={{ marginBottom: 24 }}>{t('aboutIntro.title')}</h2>
              {aboutParagraphs.map((p, i) => (
                <p className="mgc-lead" key={i} style={{ marginBottom: 16 }}>{p}</p>
              ))}
              <div style={{ marginTop: 24 }}>
                <Link to="/about" className="btn-default">{t('aboutIntro.cta')}</Link>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 56 }}>
            <AccreditationStats />
          </div>
        </div>
      </section>

      {/* Services / capability preview */}
      <section className="mgc-section mgc-section--light">
        <div className="container">
          <div className="mgc-section-head">
            <div>
              <h2 className="mgc-heading">{t('services.title')}</h2>
            </div>
            <p className="mgc-lead">{t('services.description')}</p>
          </div>
          <FadeInUp className="mgc-cards">
            {services.map((item, i) => (
              <div className="mgc-card" key={i}>
                <div className="mgc-card-icon">
                  <i className={`fa-solid ${homeData.services[i]?.icon || 'fa-building'}`} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </FadeInUp>
          <div className="mgc-section-cta">
            <Link to="/services" className="btn-default">{t('common.viewServices')}</Link>
          </div>
        </div>
      </section>

      {/* Selected projects */}
      <section className="mgc-section">
        <div className="container">
          <div className="mgc-section-head">
            <div>
              <h2 className="mgc-heading">{t('home.projects.title')}</h2>
            </div>
            <p className="mgc-lead">{t('home.projects.description')}</p>
          </div>
          {error && <p style={{ color: '#dc3545' }}>{error}</p>}
          {portfolios.length > 0 && (
            <Swiper
              className="projects-swiper"
              modules={[Autoplay]}
              spaceBetween={26}
              slidesPerView={1.1}
              autoplay={{ delay: 3500, disableOnInteraction: false }}
              breakpoints={{ 576: { slidesPerView: 2 }, 992: { slidesPerView: 3 } }}
            >
              {portfolios.map(item => (
                <SwiperSlide key={item.id}>
                  <Link to={`/portfolio/${item.slug}`} className="project-slide">
                    <div className="project-slide-image">
                      <img src={item.image || PROJECT_FALLBACK} alt={item.title} loading="lazy" />
                    </div>
                    <div className="project-slide-body">
                      <p className="project-slide-cat">{item.categories.map(c => localizedName(c, i18n.language)).join(', ')}</p>
                      <h3 className="project-slide-title">{localizedTitle(item, i18n.language)}</h3>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          <div className="mgc-section-cta">
            <Link to="/portfolio" className="btn-default">{t('home.projects.viewAll')}</Link>
          </div>
        </div>
      </section>

      {/* Why MGC (dark) */}
      <section className="mgc-section mgc-section--dark">
        <div className="container">
          <div className="mgc-section-head">
            <div>
              <span className="mgc-eyebrow">{t('whyMgc.subTitle')}</span>
              <h2 className="mgc-heading">{t('whyMgc.title')}</h2>
            </div>
          </div>
          <FadeInUp className="why-mgc-grid">
            {pillars.map((item, i) => (
              <div className="why-pillar" key={i}>
                <div className="why-pillar-icon">
                  <i className={`fa-solid ${homeData.whyMgc[i]?.icon || 'fa-award'}`} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </FadeInUp>
        </div>
      </section>

      {/* Delivery process */}
      <section className="mgc-section mgc-section--light">
        <div className="container">
          <div className="mgc-section-head">
            <div>
              <span className="mgc-eyebrow">{t('process.subTitle')}</span>
              <h2 className="mgc-heading">{t('process.title')}</h2>
            </div>
          </div>
          <div className="delivery-grid">
            {steps.map((step, i) => (
              <div className="delivery-step" key={i}>
                <span className="delivery-step-no">{step.no}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditation preview */}
      <section className="mgc-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="mgc-eyebrow">{t('accreditation.subTitle')}</span>
              <h2 className="mgc-heading" style={{ marginBottom: 24 }}>{t('accreditation.title')}</h2>
              <p className="mgc-lead" style={{ marginBottom: 28 }}>{t('accreditation.body')}</p>
              <Link to="/services" className="btn-default">{t('common.learnMore')}</Link>
            </div>
            <div className="col-lg-6">
              {accreditation && <AccreditationShowcase images={accreditation.images} maxWidth={240} />}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <ContactCta />
    </>
  );
}
